/**
 * Location-aware search intent (task #5651). Single, shared source of truth
 * for the "Mode A vs Mode B" decision that both search paths
 * (`SearchPage.tsx` = Supabase, `AlgoliaSearchPageContent.tsx` = Algolia)
 * must agree on, so a future refactor of either page can't silently
 * re-introduce the "No cars found" dead end.
 *
 *  - Mode A (`!isLocationActive`): the filters are the intent, geography is
 *    not. Return ALL matches nationwide and never constrain the query to an
 *    auto-zoomed bounding box.
 *  - Mode B (`isLocationActive`): the user typed a City/State/ZIP or clicked
 *    "Search this area". Filter within the area and, on empty, show the
 *    smart empty state.
 */

export interface LocationIntentInput {
  /** The typed City/State/ZIP location string (URL param), if any. */
  location: string | null | undefined;
  /**
   * True only when the user committed to an area — clicked "Search this area"
   * or opened a shared link with a restored viewport. Never set by the silent
   * auto-zoom widen or a bare pan.
   */
  userMovedMap: boolean;
}

/**
 * Mode B when true. A geographic intent is active when the user typed a
 * location OR committed to a map area. This is the single signal that drives
 * the smart empty state copy and the nationwide-vs-in-area query behaviour.
 */
export function isLocationActive({ location, userMovedMap }: LocationIntentInput): boolean {
  return !!location || userMovedMap;
}

export interface InitialAutoZoomInput extends LocationIntentInput {
  /**
   * Whether the one-shot initial auto-zoom has already fired for this search.
   * The Supabase path re-widens across steps and has no such flag, so it is
   * optional (defaults to "not fired").
   */
  autoZoomFired?: boolean;
}

/**
 * Whether the *initial* auto-zoom-out widen is allowed to run.
 *
 * This is the gate that fixes the dead end: the initial auto-zoom is the only
 * thing that writes a bounding box when no area was chosen, and a widened box
 * is exactly what bounds a rare filter (e.g. an uncommon colour) out into a
 * "No cars found" state. So the initial widen only fires when the user typed a
 * location (widen around its geocode) and hasn't already moved the map or
 * fired the one-shot. With no typed location (Mode A) it never fires, leaving
 * the query unbounded so it returns all matches nationwide.
 *
 * "Search this area" widening is a separate, user-driven path and is NOT
 * gated by this helper.
 */
export function shouldInitialAutoZoom({
  location,
  userMovedMap,
  autoZoomFired = false,
}: InitialAutoZoomInput): boolean {
  return !!location && !userMovedMap && !autoZoomFired;
}

export interface BoundsRect {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Whether two bounds rectangles are effectively the same viewport (task
 * #6286). The auto-widen loops on both search paths must treat a computed
 * "widened" box that matches the current bounds as a no-op terminal state —
 * writing identical bounds back to state/URL re-fires the search effect
 * chain without ever changing the query, which is exactly the endless
 * skeleton loop on zero-result searches. Tolerance matches the 5-decimal
 * rounding the Algolia path applies before persisting bounds to the URL.
 */
export function boundsApproximatelyEqual(
  a: BoundsRect | null | undefined,
  b: BoundsRect | null | undefined,
  epsilon = 1e-4,
): boolean {
  if (!a || !b) return false;
  return (
    Math.abs(a.north - b.north) <= epsilon &&
    Math.abs(a.south - b.south) <= epsilon &&
    Math.abs(a.east - b.east) <= epsilon &&
    Math.abs(a.west - b.west) <= epsilon
  );
}

/**
 * Zero-result settle latch (task #6286), shared by both search paths.
 *
 * Records a completed zero for the current signature. A changed signature
 * invalidates it immediately, and a later non-empty response clears it.
 * This is historical response context, not permission to render an empty
 * state while another request is pending.
 *
 * While loading, the previous latch value is preserved (this is exactly the
 * widen-in-flight window the latch exists for).
 */
export function nextSettledZeroSignature({
  loading,
  resultCount,
  filterSignature,
  previous,
}: {
  loading: boolean;
  resultCount: number;
  filterSignature: string;
  previous: string | null;
}): string | null {
  if (loading) return previous === filterSignature ? previous : null;
  return resultCount === 0 ? filterSignature : null;
}

/**
 * A pending search without usable cards gets placeholders. A prior settled
 * zero can stop widening but cannot override current request readiness.
 */
export function shouldShowSearchSkeletons({
  loading,
  visibleCount,
}: {
  loading: boolean;
  visibleCount: number;
  zeroResultSettled: boolean;
}): boolean {
  return loading && visibleCount === 0;
}

/**
 * Only a completed search may describe zero results. In-flight widening is
 * still pending; termination belongs to the bounds/no-op guards, not the UI.
 */
export function shouldShowSearchEmptyState({
  loading,
  visibleCount,
}: {
  loading: boolean;
  visibleCount: number;
  zeroResultSettled: boolean;
}): boolean {
  return visibleCount === 0 && !loading;
}

/**
 * The narrowing filters are every active refinement except the location and
 * radius intent — these are the ones named in the smart empty state and
 * offered for individual removal. Both search paths derive them identically.
 */
export function selectNarrowingFilters<T extends { key: string }>(activeFilters: T[]): T[] {
  return activeFilters.filter((f) => f.key !== "location" && f.key !== "radius");
}

export type SearchScope = "exact" | "nearby";

/**
 * Context attached to the final user-visible result. It never changes the
 * search intent: the original typed location and selected radius remain the
 * request URL, while this explains how the displayed result was recovered.
 */
export interface SearchResultMeta {
  scope: SearchScope;
  requestedRadiusMiles?: number;
  appliedRadiusMiles?: number;
  exactResultsCount: number;
  recoveryAttempted?: boolean;
}

import { NEARBY_RECOVERY_MIN_RESULTS } from "@workspace/algolia-contract/nearby-recovery";
export { NEARBY_RECOVERY_MIN_RESULTS, canRecoverNearbySearch, getNearbyRecoveryRadiusSteps } from "@workspace/algolia-contract/nearby-recovery";

/**
 * Return only progressively wider radius attempts. The exact request has
 * already run at `currentRadiusMiles`, so it must never be included again.
 */

export interface NearbyRecoveryEligibility {
  hasTypedLocation: boolean;
  hasValidGeocode: boolean;
  hasMapBounds: boolean;
  isCountrySearch: boolean;
  page?: number;
}

/**
 * Nearby recovery is deliberately narrow: only a successful first-page,
 * typed-location result with a real geocode can widen. A committed map
 * viewport and country search are already explicit geographic intents.
 */

/**
 * Keeps the first non-empty nearby attempt as the fallback if no later
 * attempt reaches the useful-result threshold.
 */
export function selectNearbyRecoveryAttempt<T extends { count: number }>({
  firstNonEmpty,
  candidate,
  isLastAttempt,
}: {
  firstNonEmpty: T | null;
  candidate: T;
  isLastAttempt: boolean;
}): { complete: boolean; result: T | null; firstNonEmpty: T | null } {
  const nextFirst = firstNonEmpty ?? (candidate.count > 0 ? candidate : null);
  if (candidate.count >= NEARBY_RECOVERY_MIN_RESULTS) {
    return { complete: true, result: candidate, firstNonEmpty: nextFirst };
  }
  if (isLastAttempt) {
    return { complete: true, result: nextFirst, firstNonEmpty: nextFirst };
  }
  return { complete: false, result: null, firstNonEmpty: nextFirst };
}

export interface DensityRadiusStepInput {
  /**
   * Sticky-to-location settle latch (task #6997). Once true, the stepper's
   * calibration cycle for the current location has finished and the stepper
   * must never re-fire — a narrowing sidebar filter (colour, car type, era,
   * price…) that drops the count below the minimum must NOT widen the radius.
   * Reset only on a genuine location change or a full area reset.
   */
  settled: boolean;
  /** Result count after the current round-trip settled. */
  resultCount: number;
  /** Minimum result count considered "dense enough" (DENSITY_MIN_RESULTS). */
  minResults: number;
  /** Ordered radius steps, e.g. [10, 25, 50, 100, 200]. */
  radiusOptions: readonly number[];
  /** The currently selected radius. */
  currentRadius: number;
  /** The largest radius the stepper may auto-select (DENSITY_MAX_AUTO_RADIUS). */
  maxAutoRadius: number;
  /**
   * The radius the stepper last requested, if a step is still propagating to
   * the URL param. Prevents duplicate re-fires with the same target radius.
   */
  lastSteppedRadius: number | null;
}

export type DensityRadiusStepDecision =
  /** Do nothing (already settled, or a step to this radius is already in flight). */
  | { action: "none" }
  /**
   * Latch as settled: either enough results at the current radius, or the max
   * auto radius has been reached while still sparse. Either way, the
   * calibration cycle for this location is over.
   */
  | { action: "settle" }
  /** Step up to `nextRadius` and remember it as the last-stepped radius. */
  | { action: "step"; nextRadius: number };

/**
 * Environment gates for the density-aware radius stepper (task #7021).
 *
 * Every property that would cause the stepper effect to bail early is listed
 * here so the conditions can be unit-tested as a pure function, independent
 * of the React effect machinery.
 */
export interface DensityStepperGateInput {
  /** True while an Algolia round-trip is still in flight. */
  searchLoading: boolean;
  /** Current pagination page (1-based). Stepper only runs on page 1. */
  currentPage: number;
  /**
   * True once the user has explicitly selected a radius via the picker.
   * The stepper must never override a manually chosen value.
   */
  radiusWasManuallySet: boolean;
  /** The typed City/State/ZIP location string (URL param), if any. */
  location: string | null | undefined;
  /** Geocoded coordinates for the typed location; null before the geocode resolves. */
  geoCoords: unknown | null | undefined;
  /**
   * Non-null when the map is in "Search this area" bounding-box mode.
   * In that mode the radius concept doesn't apply — the bounds drive the query.
   */
  mapBounds: unknown | null | undefined;
  /** True when the resolved location is a whole country (radius is meaningless). */
  resolvedLocationIsCountry: boolean;
  /**
   * True when the configure key the stepper is about to latch/step on matches
   * the key the last Algolia configure() call used (stale-settle guard,
   * task #6999).
   */
  configureKeyMatchesSettled: boolean;
  /**
   * True once at least one Algolia round-trip for the current configure inputs
   * has gone in-flight. Guards against latching on stale pre-location results
   * carried over from the previous search cycle.
   */
  hasFreshQuery: boolean;
}

/**
 * Returns `true` when all environment gates pass and the density stepper is
 * allowed to call `decideDensityRadiusStep`. Returns `false` (stepper bails)
 * when any gate condition fires.
 *
 * This is the pure extraction of the early-return guards that live in the
 * density stepper `useEffect` in `AlgoliaSearchPageContent.tsx`. Extracting
 * them here lets them be unit-tested without mounting the full component.
 */
export function shouldDensityStepperRun({
  searchLoading,
  currentPage,
  radiusWasManuallySet,
  location,
  geoCoords,
  mapBounds,
  resolvedLocationIsCountry,
  configureKeyMatchesSettled,
  hasFreshQuery,
}: DensityStepperGateInput): boolean {
  if (searchLoading) return false;
  if (currentPage > 1) return false;
  if (radiusWasManuallySet) return false;
  if (!location) return false;
  if (!geoCoords) return false;
  if (mapBounds) return false;
  if (resolvedLocationIsCountry) return false;
  if (!configureKeyMatchesSettled) return false;
  if (!hasFreshQuery) return false;
  return true;
}

/**
 * Pure settle/step decision for the density-aware radius stepper
 * (tasks #6996/#6997). The caller is responsible for the environment gates
 * (loading, pagination, manual radius, bounding-box mode, country-level
 * search, missing coords) and for resetting `settled`/`lastSteppedRadius`
 * on a genuine location change or area reset.
 */
export function decideDensityRadiusStep({
  settled,
  resultCount,
  minResults,
  radiusOptions,
  currentRadius,
  maxAutoRadius,
  lastSteppedRadius,
}: DensityRadiusStepInput): DensityRadiusStepDecision {
  if (settled) return { action: "none" };
  if (resultCount >= minResults) {
    // Enough results at the current radius — the cycle is complete for this
    // location. Latch so later filter-induced sparse rounds don't restart
    // the stepper.
    return { action: "settle" };
  }
  const currentIdx = radiusOptions.indexOf(currentRadius);
  const maxAutoIdx = radiusOptions.indexOf(maxAutoRadius);
  if (currentIdx < 0 || currentIdx >= maxAutoIdx) {
    // At (or beyond) the max auto radius with still-sparse results — the
    // stepper has done all it can for this location; latch as settled.
    return { action: "settle" };
  }
  const nextRadius = radiusOptions[currentIdx + 1];
  // Guard against re-firing with the same radius if the radius URL param
  // hasn't updated yet (e.g. isSameLocation no-op guard in updateUrlParam).
  if (lastSteppedRadius === nextRadius) return { action: "none" };
  return { action: "step", nextRadius };
}
